import { useState, useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PresignedURLTypesEnum } from '@Types/Documents';
import { UploadItem } from '@Types';
import { useUserContext, useNotificationsContext } from '@Contexts';
import { usePresignedUpload } from '@Hooks/usePresignedUpload';

export interface UploadProgress {
  tempId: string;
  filename: string;
  progress: number;
}

export interface FailedUpload {
  tempId: string;
  filename: string;
  error: string;
  file: File;
}

interface UseDirectFileUploadProps {
  fieldName: string;
  presignedUrlType: PresignedURLTypesEnum;
  additionalFields?: Record<string, any>;
  maxFiles?: number;
  onUploadStateChange?: (hasActiveUploads: boolean) => void;
}

export const useDirectFileUpload = ({
  fieldName,
  presignedUrlType,
  additionalFields,
  maxFiles,
  onUploadStateChange,
}: UseDirectFileUploadProps) => {
  const { watch, setValue, getValues, trigger, setError, clearErrors } = useFormContext();
  const { userData } = useUserContext();
  const { uploadFile } = usePresignedUpload();
  const { t } = useTranslation('common');
  const { success: successNotification, error: errorNotification } = useNotificationsContext();

  const [activeUploads, setActiveUploads] = useState<UploadProgress[]>([]);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [rejectionInfo, setRejectionInfo] = useState<{
    count: number;
    oversizedFiles?: Array<{ name: string; size: number }>;
    tooManyFiles?: boolean;
  } | null>(null);

  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const tempIdToRealIdMap = useRef<Map<string, string | number>>(new Map());

  const organizationId = userData?.organization_id;
  if (!organizationId) {
    throw new Error('DirectFileUpload requires user to have organization_id');
  }

  const fieldValue: UploadItem[] = watch(fieldName) || [];

  // Notify parent about upload state changes.
  useEffect(() => {
    onUploadStateChange?.(activeUploads.length > 0);
  }, [activeUploads.length, onUploadStateChange]);

  // Clear inline error when there are no failed uploads.
  useEffect(() => {
    if (failedUploads.length === 0) {
      setInlineError(null);
      setRejectionInfo(null);
    }
  }, [failedUploads.length]);

  const generateTempId = useCallback(
    () => `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    [],
  );

  const uploadSingleFile = useCallback(
    async (file: File, tempId: string): Promise<UploadItem | null> => {
      const abortController = new AbortController();
      abortControllers.current.set(tempId, abortController);

      try {
        setActiveUploads((prev) => [
          ...prev,
          {
            tempId,
            filename: file.name,
            progress: 0,
          },
        ]);

        const uploadedFile = await uploadFile(
          file,
          {
            presignedUrlType,
            filename: file.name,
            organizationId,
            additionalFields,
          },
          (progress) => {
            setActiveUploads((prev) =>
              prev.map((u) => (u.tempId === tempId ? { ...u, progress } : u)),
            );
          },
          abortController.signal,
        );

        return uploadedFile;
      } catch (error) {
        const errorMessage = (error as Error).message;

        setActiveUploads((prev) => prev.filter((u) => u.tempId !== tempId));
        abortControllers.current.delete(tempId);

        if (errorMessage !== 'ABORTED') {
          setFailedUploads((prev) => [
            ...prev,
            {
              tempId,
              filename: file.name,
              error: errorMessage,
              file,
            },
          ]);
        }

        return null;
      }
    },
    [uploadFile, presignedUrlType, organizationId, additionalFields],
  );

  const handleFilesSelected = useCallback(
    async (
      files: File[],
      rejectionInfo?: {
        count: number;
        oversizedFiles?: Array<{ name: string; size: number }>;
        tooManyFiles?: boolean;
      },
    ) => {
      // Handle too many files error first.
      if (rejectionInfo?.tooManyFiles) {
        setRejectionInfo(rejectionInfo);
        setError(fieldName, {
          type: 'tooManyFiles',
          message: t('file_upload.too_many_files', { maxFiles }),
        });
      } else if (rejectionInfo && rejectionInfo.count > 0) {
        // Handle oversized files error.
        setRejectionInfo(rejectionInfo);
        setError(fieldName, {
          type: 'fileSize',
          message: t('file_upload.files_exceeded_size_limit_summary'),
        });
      } else {
        setRejectionInfo(null);
        // Clear any existing errors for this field.
        clearErrors(fieldName);
      }

      if (!files.length) return;

      const uploadPromises = files.map(async (file) => {
        const tempId = generateTempId();
        const result = await uploadSingleFile(file, tempId);

        if (result) {
          // Store the mapping from tempId to real ID.
          tempIdToRealIdMap.current.set(tempId, result.id);

          const current = getValues(fieldName) || [];
          const updated = maxFiles === 1 ? [result] : [result, ...current];

          // Set the value of the field to the updated array - either a single file or an array of files.
          setValue(fieldName, updated, { shouldDirty: true, shouldValidate: false });
          // Remove the active upload from the list of active uploads.
          setActiveUploads((prev) => prev.filter((u) => u.tempId !== tempId));

          // Delete the abort controller for the active upload.
          abortControllers.current.delete(tempId);
        }

        return result;
      });

      const results = await Promise.all(uploadPromises);

      // Show notification based on upload results (excluding cancelled uploads).
      const successfulUploads = results.filter((result) => result !== null).length;
      const failedUploads = results.filter((result) => result === null).length;
      const totalUploadsCount = successfulUploads + failedUploads;
      const hadOversizedFiles = rejectionInfo && rejectionInfo.count > 0;

      if (totalUploadsCount > 0) {
        if (successfulUploads === totalUploadsCount && !hadOversizedFiles) {
          successNotification(t('file_upload.files_upload_all_success'));
        } else if (successfulUploads > 0) {
          errorNotification(t('file_upload.files_upload_partial_success'));
        } else {
          errorNotification(t('file_upload.files_upload_all_failed'));
        }
      }
    },
    [
      uploadSingleFile,
      getValues,
      setValue,
      fieldName,
      maxFiles,
      generateTempId,
      t,
      successNotification,
      errorNotification,
      clearErrors,
      setError,
    ],
  );

  const deleteFile = useCallback(
    (id: string | number) => {
      const idStr = String(id);

      // Handle active upload cancellation.
      const activeUpload = activeUploads.find((u) => u.tempId === idStr);
      if (activeUpload) {
        const abortController = abortControllers.current.get(idStr);
        if (abortController) {
          abortController.abort();
          abortControllers.current.delete(idStr);
        }
        setActiveUploads((prev) => prev.filter((u) => u.tempId !== idStr));
        tempIdToRealIdMap.current.delete(idStr); // Clean up mapping.
        return;
      }

      // Handle failed upload removal.
      const failedUpload = failedUploads.find((f) => f.tempId === idStr);
      if (failedUpload) {
        setFailedUploads((prev) => prev.filter((f) => f.tempId !== idStr));
        tempIdToRealIdMap.current.delete(idStr);
        return;
      }

      // Check if this is a tempId that has been mapped to a real ID.
      const realId = tempIdToRealIdMap.current.get(idStr);
      const idToDelete = realId !== undefined ? realId : id;

      // Handle completed file removal - need to handle both string and number IDs.
      const currentFiles = getValues(fieldName) || [];

      // Convert both to same type for comparison.
      const updated = currentFiles.filter((doc: UploadItem) => {
        // Compare as strings to handle mixed types.
        return String(doc.id) !== String(idToDelete);
      });

      // Set the value of the field to the updated array - either a single file or an array of files.
      setValue(fieldName, updated, { shouldDirty: true, shouldValidate: true });
      trigger(fieldName);

      // Clean up the mapping if we used it.
      if (realId !== undefined) {
        tempIdToRealIdMap.current.delete(idStr);
      }
    },
    [activeUploads, failedUploads, fieldName, getValues, setValue, trigger],
  );

  const retryUpload = useCallback(
    async (tempId: string) => {
      const failed = failedUploads.find((f) => f.tempId === tempId);
      if (!failed) return;

      setFailedUploads((prev) => prev.filter((f) => f.tempId !== tempId));
      tempIdToRealIdMap.current.delete(tempId);

      const newTempId = generateTempId();
      const result = await uploadSingleFile(failed.file, newTempId);

      if (result) {
        // Store the mapping from tempId to real ID.
        tempIdToRealIdMap.current.set(newTempId, result.id);

        const current = getValues(fieldName) || [];
        const updated = maxFiles === 1 ? [result] : [result, ...current];
        setValue(fieldName, updated, { shouldDirty: true, shouldValidate: true });
        trigger(fieldName);

        // Clean up active uploads and abort controller.
        setActiveUploads((prev) => prev.filter((u) => u.tempId !== newTempId));
        abortControllers.current.delete(newTempId);
      }
    },
    [
      failedUploads,
      uploadSingleFile,
      getValues,
      setValue,
      fieldName,
      maxFiles,
      trigger,
      generateTempId,
    ],
  );

  const getProgressById = useCallback(
    (id: string) => {
      const upload = activeUploads.find((u) => u.tempId === id);
      return upload?.progress || 0;
    },
    [activeUploads],
  );

  return {
    // State.
    activeUploads,
    failedUploads,
    localCompletedFiles: fieldValue,
    inlineError,
    rejectionInfo,
    currentCount: fieldValue.length + activeUploads.length,

    // Actions.
    handleFilesSelected,
    deleteFile,
    retryUpload,
    getProgressById,
  };
};
